import { redirect } from '@tanstack/react-router';
import { getSelectedCustomersFromIndexedDb } from '../store/jotai/customers';
import PermissionsService from '../Services/PermissionsService';
import { getDefaultCustomer } from '../Services/UserService';

/**
 * Route Guards for TanStack Router
 *
 * These utility functions are designed to be used in route `beforeLoad` hooks
 * to handle authentication and permission checks before rendering components.
 *
 * Note: These functions try IndexedDB first, then fall back
 * to localStorage (UserService default customer). This handles the timing issue
 * when redirecting immediately after login.
 */

/**
 * Get selected customers from storage
 * Tries IndexedDB first, falls back to localStorage default customer
 */
async function getSelectedCustomers(): Promise<number[]> {
  // Try IndexedDB first (Recoil atom persistence)
  const customersFromIndexedDb = await getSelectedCustomersFromIndexedDb();

  if (customersFromIndexedDb && customersFromIndexedDb.length > 0) {
    return customersFromIndexedDb;
  }

  // Fallback: Get from localStorage (set during login)
  const defaultCustomer = getDefaultCustomer();
  return defaultCustomer || [];
}

/**
 * Check if user has required permissions for a route
 *
 * @param permission - Permission constant (e.g., PERMISSION_ALARM)
 * @param actions - Array of required actions (e.g., ['read'], ['read', 'update'])
 * @param redirectTo - Optional custom redirect path (defaults to '/unauthorized')
 * @throws Redirect to unauthorized page if permission check fails
 *
 * @example
 * // OLD WAY (still supported but not recommended):
 * export const Route = createFileRoute('/_authenticated/alarm/')({
 *   beforeLoad: () => requirePermission(PERMISSION_ALARM, ['read']),
 *   component: AlarmRoute,
 * });
 *
 * @example
 * // RECOMMENDED WAY (using context from _authenticated layout):
 * export const Route = createFileRoute('/_authenticated/alarm/')({
 *   beforeLoad: ({ context }) => requirePermission(PERMISSION_ALARM, ['read'], '/unauthorized', context),
 *   component: AlarmRoute,
 * });
 */
export async function requirePermission(
  permission: string,
  actions: string[],
  redirectTo: string = '/unauthorized',
  context?: { selectedCustomers?: number[] }
): Promise<void> {
  // Get selected customers from context if available, otherwise fetch from storage
  const selectedCustomers = context?.selectedCustomers ?? (await getSelectedCustomers());

  const hasAccess = actions.every((action) =>
    PermissionsService.hasAccess(permission, [action], selectedCustomers)
  );

  if (!hasAccess) {
    throw redirect({ to: redirectTo });
  }
}

export async function requirePermissions(
  permissions: string[],
  actions: string[],
  redirectTo: string = '/unauthorized',
  context?: { selectedCustomers?: number[] }
): Promise<void> {
  // Get selected customers from context if available, otherwise fetch from storage
  const selectedCustomers = context?.selectedCustomers ?? (await getSelectedCustomers());

  const hasAccess = permissions.some((permission) =>
    actions.every((action) => PermissionsService.hasAccess(permission, [action], selectedCustomers))
  );

  if (!hasAccess) {
    throw redirect({ to: redirectTo });
  }
}

/**
 * Check if user has at least ONE of the specified permissions (OR logic)
 *
 * @param permissions - Array of permission checks: [permission, actions][]
 * @param redirectTo - Optional custom redirect path
 * @throws Redirect to unauthorized page if no permissions match
 *
 * @example
 * // OLD WAY (still supported but not recommended):
 * export const Route = createFileRoute('/_authenticated/online_panel/')({
 *   beforeLoad: () => requireAnyPermission([
 *     [PERMISSION_ONLINE_MAP_TEMPLATES_CUSTOMER, ['read']],
 *     [PERMISSION_ONLINE_MAP_TEMPLATES_PRIVATE, ['read']],
 *     [PERMISSION_ONLINE_MAP_TEMPLATES_SYSTEM, ['read']],
 *   ]),
 *   component: OnlinePanelRoute,
 * });
 *
 * @example
 * // RECOMMENDED WAY (using context from _authenticated layout):
 * export const Route = createFileRoute('/_authenticated/online_panel/')({
 *   beforeLoad: ({ context }) => requireAnyPermission(
 *     [
 *       [PERMISSION_ONLINE_MAP_TEMPLATES_CUSTOMER, ['read']],
 *       [PERMISSION_ONLINE_MAP_TEMPLATES_PRIVATE, ['read']],
 *       [PERMISSION_ONLINE_MAP_TEMPLATES_SYSTEM, ['read']],
 *     ],
 *     '/unauthorized',
 *     context
 *   ),
 *   component: OnlinePanelRoute,
 * });
 */
export async function requireAnyPermission(
  permissions: Array<[string, string[]]>,
  redirectTo: string = '/unauthorized',
  context?: { selectedCustomers?: number[] }
): Promise<void> {
  // Get selected customers from context if available, otherwise fetch from storage
  const selectedCustomers = context?.selectedCustomers ?? (await getSelectedCustomers());

  const hasAnyAccess = permissions.some(([permission, actions]) =>
    actions.every((action) => PermissionsService.hasAccess(permission, [action], selectedCustomers))
  );

  if (!hasAnyAccess) {
    throw redirect({ to: redirectTo });
  }
}
